import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { LINK_APP_STATE, LINK_RETURN_PATH } from '../account/api';
import { completeSocialSignIn, signInOutcome } from './api';

/**
 * Where nauth sends the browser back after Google. The redirect carries either
 * a single-use `exchangeToken` or an `error`; `appState` is where the user was
 * originally heading.
 */
export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /**
   * The same callback serves two journeys: signing in, and linking Google to an
   * account from Account → Sign-in methods. Only `appState` tells them apart —
   * it round-trips through the provider on the error path as well as the happy
   * one — and when a link attempt fails the person is still signed in, so
   * sending them to /signin would be wrong.
   */
  const isLink = params.get('appState') === LINK_APP_STATE;

  // The exchange token is single-use, and StrictMode runs effects twice in
  // development: a second exchange would fail after a successful sign-in.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const providerError = params.get('error');
    const exchangeToken = params.get('exchangeToken');
    const appState = params.get('appState');

    // `appState` survived a round trip through the provider — only an in-app
    // path may be used, or this is an open redirect.
    const next = appState && appState.startsWith('/') && !appState.startsWith('//') ? appState : '/';

    if (providerError === 'SIGNUP_DISABLED') {
      setError(
        isLink
          ? 'That Google address has no Rundown account, so there was nothing to link it to. Try again and choose the Google account with the same address as your Rundown account.'
          : 'There is no Rundown account for that Google address. Ask your project lead to add you.',
      );
      return;
    }
    if (providerError !== null) {
      setError(
        params.get('error_description') ??
          (isLink
            ? 'Linking was cancelled or refused at Google.'
            : 'Google sign-in was cancelled or refused.'),
      );
      return;
    }
    if (exchangeToken === null) {
      setError(
        isLink
          ? 'The link did not complete. Start again from Account → Sign-in methods.'
          : 'The sign-in link is incomplete. Start again from the sign-in page.',
      );
      return;
    }

    const finish = async (): Promise<void> => {
      try {
        const outcome = signInOutcome(await completeSocialSignIn(exchangeToken));
        if (outcome.kind !== 'signed-in') {
          setError('This account needs an extra sign-in step Rundown does not support yet.');
          return;
        }
        // Refetch before navigating so the guard does not see a stale 401.
        await queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
        navigate(next, { replace: true });
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Sign-in could not be completed.');
      }
    };

    void finish();
  }, [params, navigate, queryClient, isLink]);

  if (error) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-surface px-4">
        <div className="max-w-sm text-center">
          <img src="/rundown-logo.png" alt="Rundown" className="mx-auto h-12 w-auto" />
          <h1 className="mt-6 text-base font-semibold text-ink">
            {isLink ? 'Google was not linked' : 'Sign-in did not complete'}
          </h1>
          <p className="mt-2 text-sm text-muted">{error}</p>
          <button
            type="button"
            onClick={() => navigate(isLink ? LINK_RETURN_PATH : '/signin', { replace: true })}
            className="mt-4 text-sm font-medium text-accent hover:underline"
          >
            {isLink ? 'Back to your account' : 'Back to sign in'}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-surface px-4">
      <img src="/rundown-logo.png" alt="Rundown" className="h-12 w-auto" />
      <p className="text-sm text-muted">Signing you in…</p>
    </main>
  );
}
